import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  People,
  VpnKey,
  HealthAndSafety,
  Security,
  Assessment,
  Warning,
  CheckCircle,
  Refresh,
  TrendingUp,
  Storage,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalApiKeys: number;
  activeApiKeys: number;
  totalPrompts: number;
  recentPrompts: number;
  systemHealth: {
    status: 'healthy' | 'warning' | 'error';
    database: boolean;
    redis: boolean;
    uptime: string;
  };
  recentActivity: Array<{
    type: string;
    message: string;
    timestamp: string;
  }>;
}

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      fetchAdminStats();
    }
  }, [user]);

  const fetchAdminStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the dedicated admin system-stats endpoint
      const statsResponse = await axios.get('/api/admin/system-stats');
      const systemStats = statsResponse.data;
      
      const adminStats: AdminStats = {
        totalUsers: systemStats.users?.total || 0,
        activeUsers: systemStats.users?.active || 0,
        totalApiKeys: systemStats.api_keys?.active || 0,
        activeApiKeys: systemStats.api_keys?.active || 0,
        totalPrompts: systemStats.prompts?.total || 0,
        recentPrompts: 0, // Not provided by system-stats, could be added later
        systemHealth: {
          status: 'healthy',
          database: true,
          redis: true,
          uptime: 'Running'
        },
        recentActivity: [
          {
            type: 'info',
            message: 'System monitoring active',
            timestamp: new Date().toISOString()
          }
        ]
      };

      setStats(adminStats);
    } catch (error: any) {
      console.error('Failed to fetch admin stats:', error);
      setError('Failed to load admin statistics');
      toast.error('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };

  const getHealthStatus = () => {
    if (!stats) return { color: 'default' as const, icon: <HealthAndSafety /> };
    
    switch (stats.systemHealth.status) {
      case 'healthy':
        return { color: 'success' as const, icon: <CheckCircle /> };
      case 'warning':
        return { color: 'warning' as const, icon: <Warning /> };
      case 'error':
        return { color: 'error' as const, icon: <Warning /> };
      default:
        return { color: 'default' as const, icon: <HealthAndSafety /> };
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Access denied. You need admin privileges to access the admin panel.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchAdminStats}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  const healthStatus = getHealthStatus();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Admin Panel
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchAdminStats}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        {/* User Statistics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h4" component="h2">
                    {stats?.totalUsers || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats?.activeUsers || 0} active
                  </Typography>
                </Box>
                <People sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* API Keys Statistics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active API Keys
                  </Typography>
                  <Typography variant="h4" component="h2">
                    {stats?.activeApiKeys || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats?.totalApiKeys || 0} total
                  </Typography>
                </Box>
                <VpnKey sx={{ fontSize: 40, color: 'secondary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Prompts Statistics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Prompts
                  </Typography>
                  <Typography variant="h4" component="h2">
                    {stats?.totalPrompts || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Processed requests
                  </Typography>
                </Box>
                <Assessment sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* System Health */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    System Health
                  </Typography>
                  <Chip
                    icon={healthStatus.icon}
                    label={stats?.systemHealth.status.toUpperCase() || 'UNKNOWN'}
                    color={healthStatus.color}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {stats?.systemHealth.uptime || 'Unknown'}
                  </Typography>
                </Box>
                <HealthAndSafety sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* System Components Status */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
              <Storage sx={{ mr: 1 }} />
              System Components
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color={stats?.systemHealth.database ? 'success' : 'error'} />
                </ListItemIcon>
                <ListItemText 
                  primary="Database Connection" 
                  secondary={stats?.systemHealth.database ? 'Connected' : 'Disconnected'}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color={stats?.systemHealth.redis ? 'success' : 'error'} />
                </ListItemIcon>
                <ListItemText 
                  primary="Redis Cache" 
                  secondary={stats?.systemHealth.redis ? 'Connected' : 'Disconnected'}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Security color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="Security Services" 
                  secondary="All security checks active"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
        
        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
              <TrendingUp sx={{ mr: 1 }} />
              Quick Actions
            </Typography>
                         <Grid container spacing={2}>
               <Grid item xs={6}>
                 <Button
                   fullWidth
                   variant="outlined"
                   startIcon={<People />}
                   onClick={() => navigate('/users')}
                 >
                   Manage Users
                 </Button>
               </Grid>
               <Grid item xs={6}>
                 <Button
                   fullWidth
                   variant="outlined"
                   startIcon={<VpnKey />}
                   onClick={() => navigate('/api-keys')}
                 >
                   API Keys
                 </Button>
               </Grid>
               <Grid item xs={6}>
                 <Button
                   fullWidth
                   variant="outlined"
                   startIcon={<Security />}
                   onClick={() => navigate('/security-rules')}
                 >
                   Security Rules
                 </Button>
               </Grid>
               <Grid item xs={6}>
                 <Button
                   fullWidth
                   variant="outlined"
                   startIcon={<Assessment />}
                   onClick={() => navigate('/prompt-history')}
                 >
                   View Prompts
                 </Button>
               </Grid>
             </Grid>
          </Paper>
        </Grid>
        
        {/* System Information */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              System Administration
            </Typography>
            <Typography color="textSecondary" paragraph>
              Welcome to the GuardLLM Admin Panel. Here you can monitor system health, 
              manage users, configure security rules, and oversee API usage.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Current User
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {user?.email} ({user?.role})
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" gutterBottom>
                  System Version
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  GuardLLM v1.0.0
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Last Updated
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {new Date().toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Environment
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Development
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminPanel; 